import { HttpClient } from '@angular/common/http'
import { firstValueFrom } from 'rxjs'
import { gte as semverGTE } from 'semver'

import { Logger } from 'common/log'
import { Subclip } from 'elements-sdk'
import { BaseInterface, loadAssetContentsPages } from './base'

interface CEPWindow extends Window {
  __adobe_cep__?: any
}
declare let window: CEPWindow

export class CEPPanelInterface extends BaseInterface {
  logger = new Logger(__filename)
  native = true
  uri = false
  systemPath = window['SystemPath']
  extensionServerPort = null

  private httpClient: HttpClient
  private cs: any

  constructor (injector) {
    super(injector)

    this.httpClient = this.injector.get(HttpClient)
    this.cs = new window['CSInterface']()
  }

  async init () {
    this.logger.info('CEP init')
    const response = await firstValueFrom(this.httpClient.get('/static/build/extendscript.js', { responseType: 'text' }))
    this.app = this.getApp()
    this.platform = this.getPlatform()

    this.cs.addEventListener(
      'com.syslink.elements.server.announcePort',
      (event) => {
        this.extensionServerPort = event.data
      },
    )

    this.cs.dispatchEvent({
      type: 'com.syslink.elements.extension.requestPort',
      scope: 'APPLICATION',
      appId: undefined,
      extensionId: undefined,
    })

    const scriptPrologue = `
      global = window = this;

      var babelHelpers = global.babelHelpers = {};
      babelHelpers['typeof'] = function (x) {
        return typeof x
      };
    `

    await this.evalScript(scriptPrologue + response)

    if (this.app === 'PPRO') {
      if (semverGTE(this.cs.getHostEnvironment().appVersion, '15.0.0')) {
        this.evalScript('app.premierePro = app.premiereProNWS')
      }
      this.evalScript('app.premierePro.init()')
    }

    this.active = true
  }

  getFS () {
    return window['cep'].fs
  }

  getApp () {
    return this.cs.getHostEnvironment().appId
  }

  getPlatform () {
    return this.cs.getOSInformation().indexOf('OS X') !== -1 ? 'osx' : 'win'
  }

  getSystemPath (name) {
    return this.cs.getSystemPath(name)
  }

  evalScript<R> (script, silent?): Promise<R> {
    if (!silent) {
      this.logger.info('Running ExtendScript:', script.substring(0, 2000))
    }
    script = `var value = (function () {\n${script}\n})() || null; app.JSON3.stringify(value)`
    return new Promise(resolve => {
      this.cs.evalScript(script, result => {
        try {
          if (result === 'undefined') {
            result = undefined
          } else {
            result = JSON.parse(result)
          }
        } catch (e) {
          console.error('ExtendScript result parse failed', e)
          console.error('Result:', result)
          console.error('Code:', script)
        }
        if (!silent) {
          this.logger.debug('Result:', result)
        }
        resolve(result as R)
      })
    })
  }

  hasRestart () {
    let result = false
    JSON.parse(window.__adobe_cep__.getExtensions()).forEach((ext) => {
      if (ext.id === 'com.syslink.elements.server') {
        result = true
      }
    })
    return (result as boolean) && !!this.extensionServerPort
  }

  restart () {
    this.logger.info('Restarting extension')
    location.href = `http://127.0.0.1:${this.extensionServerPort}/`
  }

  async open (item) {
    if (item.asset?.type === 'fcpxml') {
      if (item.mainfile) {
        this.importEditorProject(item.mainfile.id)
      } else {
        this.notify.error('Internal error: bundle mainfile unknown')
      }
    } else if (item.asset?.type === 'prproj' && this.app !== 'Resolve') {
      this.importAsset(item.asset)
    } else if (['video', 'audio', 'image'].includes(item.asset.type)) {
      await this.importBundles([item]).catch(error => this.notify.error(error.toString()))
    }
  }

  async importSubclips (path, subclips: Subclip[]) {
    if (this.app === 'PPRO') {
      const items = subclips.map(item => {
        return { name: item.name, t_in: item.tIn, t_out: item.tOut }
      })

      this.evalScript(`app.premierePro.importSubclips(
        ${JSON.stringify(path)}, ${JSON.stringify(items)})`)
    }
  }

  importPath (path) {
    if (this.app === 'PPRO') {
      this.evalScript(`app.premierePro.importAssets([${JSON.stringify(path)}])`)
    }
    if (this.app === 'AEFT') {
      this.evalScript(`app.afterEffects.importAsset(${JSON.stringify(path)})`)
    }
  }

  async importDirs (dirs, folder?) {
    folder = folder || ''

    const importFolderBefore = await this.getImportFolder()
    try {
      for (const dir of dirs) {
        await loadAssetContentsPages(this.api, dir, async (bundles, directories) => {
          const importFolder = `${folder}/${dir.name}`
          await this.setImportFolder(importFolder)

          if (bundles.length) {
            await this.importBundles(bundles)
          }
          if (directories.length) {
            await this.importDirs(directories, importFolder)
          }
        })
      }
    } finally {
      await this.setImportFolder(importFolderBefore)
    }
  }

  async importBundles (bundles) {
    const subclips = await this.querySubclipImport(bundles)
    const importFolder = await this.getImportFolder()
    await super.importBundles(bundles)

    if (subclips.length) {
      for (const bundle of bundles) {
        const bundleSubclips = subclips.filter(item => item.asset.id === bundle.asset.id)
        if (!subclips.length) {
          continue
        }
        const url = await this.clientSideUrl.getPathUrl(bundle.mainfile.fullPath, this.platform)
        await this.setImportFolder(importFolder)
        await this.importSubclips(url, bundleSubclips)
      }
    }
  }

  getLocalPath (options) {
    const fs = this.getFS()
    const result = fs.showOpenDialog(
      false,
      options.properties.includes('openDirectory'),
      'Path',
      options.defaultPath,
    )
    if (result.data.length) {
      if (result.data[0].startsWith('file://')) {
        return decodeURI(result.data[0]).replace(/^(file:\/\/)/, '')
      } else {
        return result.data[0]
      }
    }
  }

  getConfig (key) {
    return window.localStorage.getItem(key)
  }

  setConfig (key, value) {
    window.localStorage.setItem(key, value)
  }

  hasConfig () {
    return true
  }

  getImportFolder () {
    return this.evalScript<string>(`return app.premierePro.getImportBin()`)
  }

  setImportFolder (folder?) {
    return this.evalScript(`return app.premierePro.setImportBin(${JSON.stringify(folder)})`)
  }

  getActiveSequencePaths () {
    return this.evalScript<string[]>('return app.premierePro.getActiveSequenceFilePaths()')
  }

  getProjectPaths () {
    return this.evalScript<string[]>('return app.premierePro.getProjectFilePaths()')
  }

  attachProxies (pathProxyMap) {
    this.evalScript(`app.premierePro.attachProxies(${JSON.stringify(pathProxyMap)})`)
  }

  isProxyMode (): Promise<boolean> {
    return this.evalScript<boolean>('return app.getEnableProxies()', true)
  }

  setProxyMode (enabled) {
    return this.evalScript(`app.setEnableProxies(${JSON.stringify(enabled)})`, true)
  }
}
